import { Pressable, View, StyleSheet, Platform, type StyleProp, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText } from './Text';

type FloatingActionButtonProps = {
  onPress: () => void;
  icon?: keyof typeof Ionicons.glyphMap;
  label?: string;
  variant?: 'primary' | 'secondary';
  accessibilityLabel?: string;
  accessibilityHint?: string;
  floating?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function FloatingActionButton({ 
  onPress, 
  icon = 'add-outline',
  label,
  variant = 'primary',
  accessibilityLabel,
  accessibilityHint,
  floating = true,
  style,
}: FloatingActionButtonProps) {
  const theme = useTheme();

  // Generate default accessibility label from icon and label
  const defaultLabel = accessibilityLabel || label || 'Action button';
  const defaultHint = accessibilityHint || (label ? `Tap to ${label.toLowerCase()}` : 'Tap to perform action');

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={defaultLabel}
      accessibilityHint={defaultHint}
      style={({ pressed }) => [
        floating ? styles.floatingButton : styles.inlineButton,
        {
          backgroundColor: variant === 'primary' ? theme.colors.primary : theme.colors.surface,
          borderColor: variant === 'primary' ? theme.colors.primary : theme.colors.border,
          borderWidth: variant === 'secondary' ? 1 : 0,
          opacity: pressed ? 0.8 : 1,
          ...(Platform.OS === 'web' ? {
            boxShadow: `0 4px 8px rgba(0, 0, 0, 0.3)`,
          } : {
            shadowColor: theme.colors.primary,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }),
        },
        style,
      ]}
    >
      <Ionicons 
        name={icon} 
        size={24} 
        color={variant === 'primary' ? (theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF') : theme.colors.primary} 
      />
      {label && (
        <ThemedText 
          variant="bodyStrong" 
          style={{ 
            color: variant === 'primary' ? (theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF') : theme.colors.primary,
            marginLeft: theme.spacing(0.5),
            fontSize: 14,
          }}
        >
          {label}
        </ThemedText>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  floatingButton: {
    minWidth: 56, // Ensure minimum touch target (56 > 44)
    minHeight: 56, // Ensure minimum touch target (56 > 44)
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    paddingHorizontal: 16,
  },
  inlineButton: {
    minWidth: 56,
    minHeight: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    paddingHorizontal: 16,
  },
});
